import _ from 'lodash';

import { contentfulClient } from 'src/config/contentfulClient';
import { FAQPage } from 'src/modules/faq/models/faq';
import { Video } from 'src/modules/videos/models/Videos';

enum ContentType {
    Video = 'video',
    FAQPage = 'faqPage',
}

interface ContentfulRecord {
    sys: any;
    fields: any;
}

interface ContentfulResponse {
    items: ContentfulRecord[];
}

export class Contentful {
    private async getAllEntriesOfSpecificType(
        type: string,
        language: string = 'en-US',
        additionalQuery: {} = {},
    ): Promise<ContentfulRecord[]> {
        const data: ContentfulResponse = await contentfulClient.getEntries({
            content_type: type,
            locale: language,
            ...additionalQuery,
        });

        return data.items;
    }

    private async prepareEntries<T>(
        type: ContentType,
        mapper: (entry: ContentfulRecord) => T,
        sorter?: (entry: T) => number,
    ): Promise<T[]> {
        const entries = await this.getAllEntriesOfSpecificType(type);

        const mappedEntries = _.map(entries, mapper);

        return sorter ? _.sortBy(mappedEntries, sorter) : mappedEntries;
    }

    private extractFileUrl(data: ContentfulRecord, field: string): string {
        return _.get(data, `${field}.fields.file.url`);
    }

    public async getAllVideos(): Promise<Video[]> {
        return this.prepareEntries<Video>(ContentType.Video, ({ fields }) => ({
            id: fields.id,
            url: this.extractFileUrl(fields, 'videoFile'),
        }));
    }

    public async getAllFAQPages(): Promise<FAQPage[]> {
        return this.prepareEntries<FAQPage>(ContentType.FAQPage, ({ fields }) => ({
            slug: fields.url,
            content: fields.content,
            name: fields.name,
        }));
    }

    public async getFAQPageBySlug(slug: string): Promise<FAQPage> {
        const [{ fields }] = await this.getAllEntriesOfSpecificType(
            ContentType.FAQPage,
            undefined,
            {
                'fields.url': slug,
            },
        );

        return {
            slug: fields.url,
            content: fields.content,
            name: fields.name,
        };
    }
}
